(function () {
  'use strict';

  function debounce(fn, ms) {
    let t;
    return function () {
      const args = arguments;
      const ctx = this;
      clearTimeout(t);
      t = setTimeout(() => fn.apply(ctx, args), ms);
    };
  }

  function formatValue(n, out) {
    if (!Number.isFinite(n)) return '—';
    const decimals = out.decimals != null ? out.decimals : 0;
    // Group thousands (en-US) so headline figures read $57,460 not $57460.
    // Fixed locale keeps output consistent regardless of the visitor's locale.
    const fixed = n.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
    return (out.prefix || '') + fixed + (out.postfix || '');
  }

  // ---------------------------------------------------------------------------
  // Safe expression parser. Hand-rolled recursive-descent.
  // Grammar:
  //   expr   = term (('+' | '-') term)*
  //   term   = power (('*' | '/') power)*
  //   power  = factor ('^' power)?        // right-associative; used by the financing amortization formula
  //   factor = NUMBER | IDENT | '(' expr ')' | '-' factor
  // No eval, no Function() — anything outside this grammar throws at parse time.
  // ---------------------------------------------------------------------------

  function tokenize(src) {
    const out = [];
    let i = 0;
    while (i < src.length) {
      const c = src[i];
      if (/\s/.test(c)) { i++; continue; }
      if ('+-*/^()'.includes(c)) { out.push({ type: c }); i++; continue; }
      if (/[0-9.]/.test(c)) {
        let j = i;
        while (j < src.length && /[0-9.]/.test(src[j])) j++;
        out.push({ type: 'number', value: parseFloat(src.slice(i, j)) });
        i = j;
        continue;
      }
      if (/[a-z_]/i.test(c)) {
        let j = i;
        while (j < src.length && /[a-z0-9_]/i.test(src[j])) j++;
        out.push({ type: 'ident', value: src.slice(i, j) });
        i = j;
        continue;
      }
      throw new Error('lt-calc: bad character in formula: ' + c);
    }
    return out;
  }

  function parseFormula(src) {
    const tokens = tokenize(src);
    let pos = 0;
    const peek = () => tokens[pos];
    const consume = () => tokens[pos++];

    function parseExpr() {
      let left = parseTerm();
      while (peek() && (peek().type === '+' || peek().type === '-')) {
        const op = consume().type;
        left = { op, left, right: parseTerm() };
      }
      return left;
    }
    function parseTerm() {
      let left = parsePower();
      while (peek() && (peek().type === '*' || peek().type === '/')) {
        const op = consume().type;
        left = { op, left, right: parsePower() };
      }
      return left;
    }
    function parsePower() {
      const left = parseFactor();
      if (peek() && peek().type === '^') {
        consume();
        return { op: '^', left, right: parsePower() }; // right-associative
      }
      return left;
    }
    function parseFactor() {
      const t = peek();
      if (!t) throw new Error('lt-calc: unexpected end of formula');
      if (t.type === '-') { consume(); return { op: 'neg', operand: parseFactor() }; }
      if (t.type === 'number') { consume(); return { num: t.value }; }
      if (t.type === 'ident')  { consume(); return { ident: t.value }; }
      if (t.type === '(') {
        consume();
        const inner = parseExpr();
        if (!peek() || peek().type !== ')') throw new Error('lt-calc: missing )');
        consume();
        return inner;
      }
      throw new Error('lt-calc: unexpected token in formula: ' + JSON.stringify(t));
    }

    const ast = parseExpr();
    if (pos < tokens.length) throw new Error('lt-calc: trailing tokens after formula');
    return ast;
  }

  function evalAst(ast, vars) {
    if (ast.num !== undefined) return ast.num;
    if (ast.ident !== undefined) {
      const v = vars[ast.ident];
      if (v === undefined || v === null || v === '') return 0;
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    }
    if (ast.op === 'neg') return -evalAst(ast.operand, vars);
    const l = evalAst(ast.left, vars);
    const r = evalAst(ast.right, vars);
    switch (ast.op) {
      case '+': return l + r;
      case '-': return l - r;
      case '*': return l * r;
      case '/': return r === 0 ? 0 : l / r;
      case '^': return Math.pow(l, r);
    }
    throw new Error('lt-calc: unknown op: ' + ast.op);
  }

  // ---------------------------------------------------------------------------

  class LtCalculator {
    constructor(root) {
      this.root = root;
      const cfgEl = root.querySelector('.lt-calc-config');
      if (!cfgEl || !cfgEl.textContent.trim()) {
        console.warn('lt-calc: missing or empty config block', root);
        return;
      }
      try {
        this.config = JSON.parse(cfgEl.textContent);
      } catch (e) {
        console.warn('lt-calc: invalid config JSON', e);
        return;
      }

      // Flatten output_groups (if present) into a single list so formula
      // parse/eval and error-summary code can stay group-agnostic. Render is
      // the only thing that needs to know about groups.
      this.flatOutputs = [];
      if (Array.isArray(this.config.output_groups)) {
        for (const group of this.config.output_groups) {
          for (const out of group.outputs || []) this.flatOutputs.push(out);
        }
      } else if (Array.isArray(this.config.outputs)) {
        this.flatOutputs = this.config.outputs;
      }

      this.parsedFormulas = {};
      for (const out of this.flatOutputs) {
        if (!out.formula) continue; // gauges read an existing output, they don't compute one
        try {
          this.parsedFormulas[out.ref] = parseFormula(out.formula);
        } catch (e) {
          console.warn('lt-calc: failed to parse formula for', out.ref, e);
        }
      }

      this.lastVars = {};
      this.renderInputs();
      this.renderOutputs();
      this.attachListeners();
      this.attachTooltips();
      this.attachLeadForm();
      this.recompute();
    }

    attachTooltips() {
      const triggers = this.root.querySelectorAll('.lt-calc__tooltip-trigger');
      if (!triggers.length) return;

      const closeAll = () => {
        this.root.querySelectorAll('.lt-calc__tooltip-content[data-open="true"]').forEach(c => {
          c.dataset.open = 'false';
        });
        this.root.querySelectorAll('.lt-calc__tooltip-trigger[aria-expanded="true"]').forEach(t => {
          t.setAttribute('aria-expanded', 'false');
        });
      };

      const openOne = (tip) => {
        closeAll();
        const content = tip.parentElement.querySelector('.lt-calc__tooltip-content');
        if (!content) return;
        content.dataset.open = 'true';
        tip.setAttribute('aria-expanded', 'true');

        // Keep the popover inside the viewport.
        //
        // A binary left/right flip can't do this: anchoring right pushes a 288px
        // popover off the LEFT edge whenever the trigger sits near the left, which
        // is most of them. So clamp to the viewport instead and let the caret move
        // to keep pointing at the trigger.
        //
        // documentElement.clientWidth, NOT window.innerWidth — innerWidth grows with
        // the page's own horizontal overflow, so it reported 558px on a 320px phone
        // and every off-screen check silently passed.
        const MARGIN = 8;
        content.style.left = '0px';
        const viewport = document.documentElement.clientWidth;
        const wrap = tip.parentElement.getBoundingClientRect();
        const rect = content.getBoundingClientRect();

        let left = rect.left;
        if (left + rect.width > viewport - MARGIN) left = viewport - MARGIN - rect.width;
        if (left < MARGIN) left = MARGIN;
        content.style.left = (left - wrap.left) + 'px';

        // Caret tracks the trigger rather than the popover's corner.
        const caret = Math.max(6, Math.min(rect.width - 16, wrap.left + wrap.width / 2 - left - 5));
        content.style.setProperty('--lt-caret-x', caret + 'px');
      };

      for (const tip of triggers) {
        // Don't fire form submit — these triggers live inside <label>.
        tip.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (tip.getAttribute('aria-expanded') === 'true') {
            closeAll();
          } else {
            openOne(tip);
          }
        });
        // Click on the label shouldn't focus the input or close the tooltip.
        const content = tip.parentElement.querySelector('.lt-calc__tooltip-content');
        if (content) {
          content.addEventListener('click', (e) => e.stopPropagation());
        }
      }

      // Click anywhere outside a trigger or popover closes everything.
      document.addEventListener('click', (e) => {
        if (!e.target.closest('.lt-calc__tooltip-wrap')) closeAll();
      });

      // Esc closes any open tooltip.
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') closeAll();
      });
    }

    renderInputs() {
      const host = this.root.querySelector('.lt-calc__fields');
      if (!host) return;
      const sectionId = this.root.dataset.sectionId || 'lt-calc';

      for (const field of this.config.fields) {
        const row = document.createElement('div');
        row.className = 'lt-calc__row';

        const label = document.createElement('label');
        label.className = 'lt-calc__field-label';
        label.textContent = field.label;
        label.htmlFor = sectionId + '__' + field.ref;
        row.appendChild(label);

        if (field.hint) {
          const wrap = document.createElement('span');
          wrap.className = 'lt-calc__tooltip-wrap';

          const tipId = sectionId + '__' + field.ref + '__tip';
          const tip = document.createElement('button');
          tip.type = 'button';
          tip.className = 'lt-calc__tooltip-trigger';
          tip.setAttribute('aria-label', 'More info about ' + field.label);
          tip.setAttribute('aria-expanded', 'false');
          tip.setAttribute('aria-controls', tipId);
          tip.textContent = 'i';

          const content = document.createElement('span');
          content.className = 'lt-calc__tooltip-content';
          content.id = tipId;
          content.setAttribute('role', 'tooltip');
          content.dataset.open = 'false';
          content.textContent = field.hint;

          wrap.appendChild(tip);
          wrap.appendChild(content);
          label.appendChild(wrap);
        }

        let input;
        let appendable;
        if (field.type === 'select') {
          input = document.createElement('select');
          for (const opt of field.options || []) {
            const o = document.createElement('option');
            o.value = opt.value;
            o.textContent = opt.label;
            if (opt.value === field.default) o.selected = true;
            input.appendChild(o);
          }
        } else if (field.type === 'slider') {
          input = document.createElement('input');
          input.type = 'range';
          if (field.min !== undefined) input.min = field.min;
          if (field.max !== undefined) input.max = field.max;
          if (field.step !== undefined) input.step = field.step;
          input.value = field.default != null ? field.default : 0;
        } else {
          input = document.createElement('input');
          input.type = field.type === 'number' ? 'number' : 'text';
          if (field.min !== undefined) input.min = field.min;
          if (field.max !== undefined) input.max = field.max;
          if (field.step !== undefined) input.step = field.step;
          if (field.maxLength !== undefined) input.maxLength = field.maxLength;
          input.value = field.default != null ? field.default : '';
        }
        input.id = label.htmlFor;
        input.name = field.ref;
        input.className = 'lt-calc__field-input';
        // '__tip', not '__hint' — the tooltip element's id is built as `${...}__tip`
        // above. This pointed at an element that never existed, so every hint on every
        // calculator was silently invisible to screen readers.
        if (field.hint) input.setAttribute('aria-describedby', label.htmlFor + '__tip');
        appendable = input;

        if (field.type === 'slider') {
          // Wrap slider with a datalist (native track ticks) + a labeled ticks row below it.
          const wrap = document.createElement('div');
          wrap.className = 'lt-calc__slider-wrap';

          const datalistId = label.htmlFor + '__ticks';
          const datalist = document.createElement('datalist');
          datalist.id = datalistId;
          input.setAttribute('list', datalistId);

          const ticksRow = document.createElement('div');
          ticksRow.className = 'lt-calc__slider-ticks';

          const populateTicks = (max, interval) => {
            datalist.replaceChildren();
            ticksRow.replaceChildren();
            const min = Number(input.min) || 0;
            const step = interval || (max - min) / 5;
            const count = Math.round((max - min) / step) + 1;
            for (let i = 0; i < count; i++) {
              const v = min + i * step;
              const opt = document.createElement('option');
              opt.value = String(v);
              datalist.appendChild(opt);
              const tick = document.createElement('span');
              tick.className = 'lt-calc__slider-tick';
              const text = (Number.isInteger(v) ? v : v.toFixed(1)) + (field.postfix || '');
              tick.textContent = text;
              ticksRow.appendChild(tick);
            }
          };

          // Initial population from field.max + first matching maxByField (if any).
          let initialMax = Number(input.max) || 100;
          let initialInterval;
          if (field.maxByField) {
            const dep = this.root.querySelector('[name="' + field.maxByField.field + '"]');
            const cfg = dep && field.maxByField.values[dep.value];
            if (cfg) {
              initialMax = cfg.max;
              initialInterval = cfg.tickInterval;
              input.max = cfg.max;
              if (Number(input.value) > cfg.max) input.value = cfg.max;
            }
          }
          populateTicks(initialMax, initialInterval);

          wrap.appendChild(input);
          wrap.appendChild(datalist);
          wrap.appendChild(ticksRow);
          appendable = wrap;

          // Wire dependent-field listener so the slider's range adapts.
          if (field.maxByField) {
            const dep = this.root.querySelector('[name="' + field.maxByField.field + '"]');
            if (dep) {
              const updateRange = () => {
                const cfg = field.maxByField.values[dep.value];
                if (!cfg) return;
                input.max = cfg.max;
                populateTicks(Number(cfg.max), cfg.tickInterval);
                if (Number(input.value) > cfg.max) {
                  input.value = cfg.max;
                  input.dispatchEvent(new Event('input', { bubbles: true }));
                }
              };
              dep.addEventListener('change', updateRange);
              dep.addEventListener('input', updateRange);
            }
          }
        }

        if (field.type === 'slider') {
          // Slider sits on its own; live value readout (with postfix) goes below.
          row.appendChild(appendable);

          const valueSpan = document.createElement('span');
          valueSpan.className = 'lt-calc__slider-value';
          const decimals = field.decimals != null ? field.decimals : 0;
          const updateValue = () => {
            const v = Number(input.value);
            const num = Number.isFinite(v) ? v.toFixed(decimals) : '0';
            valueSpan.textContent = num + (field.postfix || '');
          };
          updateValue();
          input.addEventListener('input', updateValue);
          row.appendChild(valueSpan);
        } else {
          // Wrap text/number/select inputs with optional prefix/postfix.
          const control = document.createElement('div');
          control.className = 'lt-calc__field-control';
          if (field.prefix) {
            const pre = document.createElement('span');
            pre.className = 'lt-calc__field-prefix';
            pre.textContent = field.prefix;
            control.appendChild(pre);
          }
          control.appendChild(input);
          if (field.postfix) {
            const post = document.createElement('span');
            post.className = 'lt-calc__field-postfix';
            post.textContent = field.postfix;
            control.appendChild(post);
          }
          row.appendChild(control);
        }

        // A select can drive this field's value (press model -> cylinder tonnage).
        // Mirror of maxByField above, which drives a slider's range the same way.
        // A dep value with no entry (e.g. "custom") leaves whatever the user typed.
        if (field.defaultByField) {
          const dep = this.root.querySelector('[name="' + field.defaultByField.field + '"]');
          if (dep) {
            dep.addEventListener('change', () => {
              const v = field.defaultByField.values[dep.value];
              if (v === undefined) return;
              input.value = v;
              input.dispatchEvent(new Event('input', { bubbles: true }));
            });
          }
        }

        host.appendChild(row);
      }
    }

    renderOutputs() {
      const host = this.root.querySelector('.lt-calc__results');
      if (!host) return;
      if (Array.isArray(this.config.output_groups)) {
        for (const group of this.config.output_groups) {
          if (group.heading) {
            const h = document.createElement('h3');
            h.className = 'lt-calc__output-group-heading';
            h.textContent = group.heading;
            host.appendChild(h);
          }
          for (const out of group.outputs || []) {
            host.appendChild(this.renderOutputRow(out));
          }
        }
      } else {
        for (const out of this.config.outputs || []) {
          host.appendChild(this.renderOutputRow(out));
        }
      }

      // The headline band is styled off :last-child. A gauge renders after it, so
      // pin the treatment to the row explicitly — otherwise the blue would jump to
      // the gauge (or, since a gauge isn't an .lt-calc__output, vanish entirely).
      const rows = host.querySelectorAll('.lt-calc__output');
      if (rows.length) rows[rows.length - 1].classList.add('lt-calc__output--final');
    }

    // A gauge doesn't compute anything. It reads an output another row already
    // produced (out.source) and says whether it lands inside the target band for
    // whatever the user picked in out.bandsByField.field — e.g. is this platen PSI
    // right for flower vs hash. Bands come from the config, not from code.
    renderGauge(out) {
      const el = document.createElement('div');
      el.className = 'lt-calc__gauge';
      el.dataset.gauge = out.ref;

      const head = document.createElement('div');
      head.className = 'lt-calc__gauge-head';

      const label = document.createElement('span');
      label.className = 'lt-calc__gauge-label';
      label.textContent = out.label;
      head.appendChild(label);

      const verdict = document.createElement('span');
      verdict.className = 'lt-calc__gauge-verdict';
      verdict.dataset.gaugeVerdict = '';
      verdict.setAttribute('role', 'status');
      verdict.textContent = '—';
      head.appendChild(verdict);
      el.appendChild(head);

      const track = document.createElement('div');
      track.className = 'lt-calc__gauge-track';

      const band = document.createElement('div');
      band.className = 'lt-calc__gauge-band';
      band.dataset.gaugeBand = '';
      track.appendChild(band);

      const marker = document.createElement('div');
      marker.className = 'lt-calc__gauge-marker';
      marker.dataset.gaugeMarker = '';
      track.appendChild(marker);
      el.appendChild(track);

      const foot = document.createElement('div');
      foot.className = 'lt-calc__gauge-foot';
      foot.dataset.gaugeFoot = '';
      el.appendChild(foot);

      return el;
    }

    updateGauges(vars) {
      for (const out of this.flatOutputs) {
        if (out.type !== 'gauge') continue;
        const el = this.root.querySelector(`[data-gauge="${out.ref}"]`);
        if (!el) continue;

        const bands = out.bandsByField;
        const dep = bands && this.root.querySelector('[name="' + bands.field + '"]');
        const band = dep && bands.values[dep.value];
        const value = vars[out.source];
        const scaleMax = out.scale_max || 3000;

        const verdictEl = el.querySelector('[data-gauge-verdict]');
        const bandEl = el.querySelector('[data-gauge-band]');
        const markerEl = el.querySelector('[data-gauge-marker]');
        const footEl = el.querySelector('[data-gauge-foot]');

        // Fields the source formula divides by. Clear the bag length and the engine's
        // divide-by-zero guard returns a *finite* 0, which sails past Number.isFinite
        // and would let us announce "Too little pressure" about a number the user never
        // entered. Say nothing instead of saying something confident and wrong.
        const incomplete = (out.requires || []).some(ref => {
          const f = this.root.querySelector('[name="' + ref + '"]');
          return !f || f.value === '' || !(Number(f.value) > 0);
        });

        // Only write the verdict when it changes: it carries role="status", and rewriting
        // it on every debounced recompute makes a screen reader re-announce on every
        // slider tick.
        const setVerdict = (text) => {
          if (verdictEl.textContent !== text) verdictEl.textContent = text;
        };

        if (!band || incomplete || !Number.isFinite(value)) {
          el.dataset.status = 'unknown';
          setVerdict('—');
          markerEl.style.display = 'none';
          footEl.textContent = incomplete ? 'Enter your bag size to check the pressure.' : '';
          continue;
        }

        const pct = n => Math.max(0, Math.min(100, (n / scaleMax) * 100));
        bandEl.style.left = pct(band.min) + '%';
        bandEl.style.width = Math.max(0, pct(band.max) - pct(band.min)) + '%';

        markerEl.style.display = '';
        markerEl.style.left = pct(value) + '%';
        // Off the end of the scale: pin the marker and let the verdict carry the truth.
        markerEl.dataset.pinned = value > scaleMax ? 'high' : '';

        const status = value < band.min ? 'low' : value > band.max ? 'high' : 'ok';
        el.dataset.status = status;
        setVerdict(
          status === 'ok'   ? 'In range' :
          status === 'low'  ? 'Too little pressure' :
                              'Too much pressure'
        );

        const fmt = n => n.toLocaleString('en-US');
        footEl.textContent =
          `${band.label} wants ${fmt(band.min)} to ${fmt(band.max)} platen PSI. You're at ${fmt(Math.round(value))}.`;
      }
    }

    renderOutputRow(out) {
      if (out.type === 'gauge') return this.renderGauge(out);
      const sectionId = this.root.dataset.sectionId || 'lt-calc';
      const row = document.createElement('div');
      row.className = 'lt-calc__output';

      const label = document.createElement('span');
      label.className = 'lt-calc__output-label';
      label.textContent = out.label;

      if (out.hint) {
        const wrap = document.createElement('span');
        wrap.className = 'lt-calc__tooltip-wrap';

        const tipId = sectionId + '__out_' + out.ref + '__tip';
        const tip = document.createElement('button');
        tip.type = 'button';
        tip.className = 'lt-calc__tooltip-trigger';
        tip.setAttribute('aria-label', 'More info about ' + out.label);
        tip.setAttribute('aria-expanded', 'false');
        tip.setAttribute('aria-controls', tipId);
        tip.textContent = 'i';

        const content = document.createElement('span');
        content.className = 'lt-calc__tooltip-content';
        content.id = tipId;
        content.setAttribute('role', 'tooltip');
        content.dataset.open = 'false';
        content.textContent = out.hint;

        wrap.appendChild(tip);
        wrap.appendChild(content);
        label.appendChild(wrap);
      }

      const value = document.createElement('span');
      value.className = 'lt-calc__output-value';
      value.dataset.ref = out.ref;
      value.textContent = '—';

      row.appendChild(label);
      row.appendChild(value);
      return row;
    }

    attachListeners() {
      const debounced = debounce(() => this.recompute(), 150);
      this.root.querySelectorAll('input, select').forEach(el => {
        el.addEventListener('input', debounced);
        el.addEventListener('change', debounced);
      });
      // Clamp number inputs to their configured min/max on blur/change so the
      // user can't accidentally enter e.g. 500 in a 0-100 % field. Sliders
      // are already clamped by the browser; selects/text don't need it.
      for (const f of this.config.fields) {
        if (f.type !== 'number') continue;
        if (f.min == null && f.max == null) continue;
        const el = this.root.querySelector(`[name="${f.ref}"]`);
        if (!el) continue;
        const clamp = () => {
          if (el.value === '') return;
          let n = Number(el.value);
          if (!Number.isFinite(n)) return;
          if (f.min != null && n < f.min) n = f.min;
          if (f.max != null && n > f.max) n = f.max;
          if (String(n) !== el.value) {
            el.value = String(n);
            el.dispatchEvent(new Event('input', { bubbles: true }));
          }
        };
        el.addEventListener('blur', clamp);
        el.addEventListener('change', clamp);
      }
    }

    attachLeadForm() {
      const cta = this.root.querySelector('.lt-calc__cta');
      const form = this.root.querySelector('.lt-calc__lead-form');
      if (!cta || !form) return;

      cta.addEventListener('click', () => {
        cta.hidden = true;
        form.hidden = false;
        const email = form.querySelector('input[name="email"]');
        if (email) email.focus();
      });

      const errEl = form.querySelector('.lt-calc__field-error');
      const setError = (msg, focusEl) => {
        if (errEl) {
          errEl.textContent = msg;
          errEl.hidden = false;
        }
        if (focusEl) focusEl.focus();
      };
      const clearError = () => {
        if (errEl) {
          errEl.textContent = '';
          errEl.hidden = true;
        }
      };

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (form.dataset.submitting === '1') return;
        clearError();

        const emailEl = form.querySelector('input[name="email"]');
        const tierEl  = form.querySelector('select[name="press_tier_considering"]');

        if (!emailEl || !emailEl.value.trim() || !emailEl.checkValidity()) {
          setError('Please enter a valid email address.', emailEl);
          return;
        }
        if (!tierEl || !tierEl.value) {
          setError('Please choose which Lowtemp system you are considering.', tierEl);
          return;
        }

        const submitBtn = form.querySelector('.lt-calc__submit');
        form.dataset.submitting = '1';
        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.setAttribute('aria-busy', 'true');
        }

        const formData = new FormData(form);
        const result = await this.submitToHubSpot(formData);

        if (result.ok) {
          // Best-effort Shopify customer sync; runs in parallel and we don't gate
          // success on its outcome (it always 302s and we can't read iframe state).
          this.syncShopifyConsent(formData);
          form.hidden = true;
          const success = this.root.querySelector('.lt-calc__success');
          if (success) success.hidden = false;
        } else {
          delete form.dataset.submitting;
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.removeAttribute('aria-busy');
          }
          this.showError();
        }
      });
    }

    async submitToHubSpot(formData) {
      const portalId = this.root.dataset.portalId;
      const formGuid = this.root.dataset.formGuid;
      if (!portalId || !formGuid) {
        console.warn('lt-calc: missing portalId or formGuid; skipping HubSpot submit');
        return { ok: false, reason: 'missing_config' };
      }
      const url = 'https://api.hsforms.com/submissions/v3/integration/submit/' +
                  encodeURIComponent(portalId) + '/' + encodeURIComponent(formGuid);
      const vars = this.lastVars || {};

      const fields = [];
      const std = ['email', 'firstname', 'lastname', 'company', 'phone',
                   'calculator_notes', 'press_tier_considering'];
      for (const k of std) {
        const v = formData.get(k);
        if (v != null && String(v).length > 0) {
          fields.push({ objectTypeId: '0-1', name: k, value: String(v) });
        }
      }

      fields.push({
        objectTypeId: '0-1',
        name: 'calculator_type',
        value: this.config.hubspot_calculator_type || this.config.id.replace(/-/g, '_')
      });

      if (this.config.id === 'rosin-profit') {
        const totalNet = Number(vars.total_net);
        fields.push({
          objectTypeId: '0-1',
          name: 'projected_daily_profit',
          value: Number.isFinite(totalNet) ? String(Math.round(totalNet)) : '0'
        });
        // Result outputs -> dedicated HubSpot properties (rounded to whole units).
        const round0 = (n) => {
          const v = Number(n);
          return Number.isFinite(v) ? String(Math.round(v)) : '0';
        };
        fields.push({ objectTypeId: '0-1', name: 'total_grams_of_rosin',           value: round0(vars.total_grams) });
        fields.push({ objectTypeId: '0-1', name: 'gross_income',                    value: round0(vars.gross_income) });
        const weightG = Number(vars.weight) || 0;
        fields.push({
          objectTypeId: '0-1',
          name: 'daily_starting_material_lbs',
          value: (weightG / 453.592).toFixed(2)
        });
        if (vars.strain) {
          fields.push({ objectTypeId: '0-1', name: 'calculator_strain', value: String(vars.strain) });
        }
        // Also populate the shared yield properties from the Profit calculator's
        // own figures, so every Profit lead carries starting/rosin/percent too.
        // (The standalone Yield calculator has lead capture disabled, so without
        // this these three properties stay empty on Profit-calc submissions.)
        fields.push({ objectTypeId: '0-1', name: 'calculator_yield_starting', value: round0(vars.weight) });
        fields.push({ objectTypeId: '0-1', name: 'calculator_yield_rosin',    value: round0(vars.total_grams) });
        const yieldPct = Number(vars.yield_pct);
        fields.push({ objectTypeId: '0-1', name: 'calculator_yield_pct',      value: Number.isFinite(yieldPct) ? yieldPct.toFixed(2) : '0.00' });
      } else if (this.config.id === 'rosin-yield') {
        fields.push({
          objectTypeId: '0-1',
          name: 'calculator_yield_starting',
          value: String(Number(vars.starting) || 0)
        });
        fields.push({
          objectTypeId: '0-1',
          name: 'calculator_yield_rosin',
          value: String(Number(vars.rosin) || 0)
        });
        const yp = Number(vars.yield_pct);
        fields.push({
          objectTypeId: '0-1',
          name: 'calculator_yield_pct',
          value: Number.isFinite(yp) ? yp.toFixed(2) : '0.00'
        });
      } else if (this.config.id === 'mini-osprey-roi') {
        const round0 = (n) => {
          const v = Number(n);
          return Number.isFinite(v) ? String(Math.round(v)) : '0';
        };
        const systemNames = {
          '5995': 'Mini Osprey 2.0',
          '8790': 'Mini Osprey 2.0 + Mini Nest',
          '13285': 'Mini Osprey 2.0 + Mini Nest + Mini AutoSieve',
          '15595': 'Mini Lab Starter Bundle',
          '33595': 'Mini Lab Pro Bundle'
        };
        const eq = String(vars.equipment_cost || '');
        fields.push({ objectTypeId: '0-1', name: 'mini_lab_system', value: systemNames[eq] || eq });
        fields.push({ objectTypeId: '0-1', name: 'mini_lab_material_type', value: vars.material === 'dry' ? 'Dry Material' : 'Fresh Frozen' });
        fields.push({ objectTypeId: '0-1', name: 'mini_lab_biomass_grams_per_wash', value: round0(vars.material_grams) });
        fields.push({ objectTypeId: '0-1', name: 'mini_lab_washes_per_month', value: round0(vars.washes_per_month) });
        const mYp = Number(vars.yield_pct);
        fields.push({ objectTypeId: '0-1', name: 'mini_lab_yield_pct', value: Number.isFinite(mYp) ? mYp.toFixed(1) : '0.0' });
        fields.push({ objectTypeId: '0-1', name: 'mini_lab_annual_rosin_value', value: round0(vars.annual_value) });
        const mp = Number(vars.months_to_payback);
        fields.push({ objectTypeId: '0-1', name: 'mini_lab_months_to_payback', value: Number.isFinite(mp) ? mp.toFixed(1) : '0.0' });
        // Remaining fields on the dedicated Mini Osprey form (GUID 841a5c4b…): the
        // price input plus every calculated output. Names must match the form's
        // internal names EXACTLY — three of them use a DOUBLE underscore.
        const round1 = (n) => {
          const v = Number(n);
          return Number.isFinite(v) ? v.toFixed(1) : '0.0';
        };
        fields.push({ objectTypeId: '0-1', name: 'mini_lab_price_per_gram',       value: round0(vars.price_per_g) });
        fields.push({ objectTypeId: '0-1', name: 'mini_lab_rosin_per_wash',       value: round1(vars.rosin_per_wash) });
        fields.push({ objectTypeId: '0-1', name: 'mini_lab__rosin_per_month',     value: round0(vars.monthly_rosin) });
        fields.push({ objectTypeId: '0-1', name: 'mini_lab_rosin_value_per_wash', value: round0(vars.value_per_wash) });
        fields.push({ objectTypeId: '0-1', name: 'mini_lab__first_year_net',      value: round0(vars.first_year_net) });
        fields.push({ objectTypeId: '0-1', name: 'mini_lab__washes_to_pay_off',   value: round1(vars.washes_to_payback) });
      }

      const consented = formData.get('subscribed_to_marketing') === 'on';
      fields.push({
        objectTypeId: '0-1',
        name: 'subscribed_to_marketing',
        value: consented ? 'true' : 'false'
      });

      const hutkMatch = document.cookie.match(/hubspotutk=([^;]+)/);
      const hutk = hutkMatch ? hutkMatch[1] : null;

      const context = {
        pageUri: window.location.href,
        pageName: document.title
      };
      if (hutk) context.hutk = hutk;

      const body = {
        submittedAt: Date.now(),
        fields,
        context
      };

      if (consented) {
        const subTypeId = this.root.dataset.subscriptionTypeId;
        const consent = {
          consentToProcess: true,
          text: 'I agree to allow Lowtemp Industries to store and process my personal data.'
        };
        if (subTypeId) {
          consent.communications = [{
            value: true,
            subscriptionTypeId: Number(subTypeId),
            text: 'I agree to receive marketing communications from Lowtemp Industries.'
          }];
        }
        body.legalConsentOptions = { consent };
      }

      try {
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
        if (!res.ok) {
          let detail = '';
          try { detail = await res.text(); } catch (_) {}
          console.warn('lt-calc: HubSpot returned', res.status, detail);
          return { ok: false, status: res.status };
        }
        return { ok: true };
      } catch (e) {
        console.warn('lt-calc: HubSpot fetch failed', e);
        return { ok: false, reason: 'network' };
      }
    }

    showError() {
      const err = this.root.querySelector('.lt-calc__error');
      if (!err) return;
      // Clear any prior content (no innerHTML).
      while (err.firstChild) err.removeChild(err.firstChild);

      const summary = (this.flatOutputs || [])
        .map(o => o.label + ': ' + formatValue((this.lastVars || {})[o.ref] || 0, o))
        .join('\n');
      const subject = 'Rosin Calculator results';
      const bodyText = 'My results:\n\n' + summary;
      const mailto = 'mailto:sales@lowtemp-plates.com'
        + '?subject=' + encodeURIComponent(subject)
        + '&body=' + encodeURIComponent(bodyText);

      err.appendChild(document.createTextNode('Something went wrong saving your results. '));
      const link = document.createElement('a');
      link.href = mailto;
      link.textContent = 'Email sales@lowtemp-plates.com directly';
      err.appendChild(link);
      err.appendChild(document.createTextNode('.'));
      err.hidden = false;
    }

    syncShopifyConsent(formData) {
      // Posts to Shopify's customer-create endpoint via a hidden iframe so the
      // customer record gets accepts_marketing=true alongside the HubSpot lead.
      // No-ops if consent box wasn't checked.
      if (formData.get('subscribed_to_marketing') !== 'on') return;
      const email = formData.get('email');
      if (!email) return;

      const iframe = this.root.querySelector('.lt-calc__sync-iframe');
      if (!iframe) return;

      const f = document.createElement('form');
      f.method = 'POST';
      f.action = '/contact#contact_form';
      f.target = iframe.name;
      f.style.display = 'none';
      f.acceptCharset = 'UTF-8';

      const fields = {
        'form_type': 'customer',
        'utf8': '✓',
        'contact[email]': email,
        'contact[accepts_marketing]': '1',
        'contact[tags]': 'calculator-lead'
      };
      const firstname = formData.get('firstname');
      const lastname = formData.get('lastname');
      if (firstname) fields['contact[first_name]'] = firstname;
      if (lastname)  fields['contact[last_name]']  = lastname;

      for (const k in fields) {
        const i = document.createElement('input');
        i.type = 'hidden';
        i.name = k;
        i.value = fields[k];
        f.appendChild(i);
      }

      document.body.appendChild(f);
      try { f.submit(); } catch (e) { console.warn('lt-calc: shopify sync submit failed', e); }
      setTimeout(() => f.remove(), 1000);
    }

    recompute() {
      const vars = {};
      for (const f of this.config.fields) {
        const el = this.root.querySelector(`[name="${f.ref}"]`);
        if (!el) {
          vars[f.ref] = f.default;
          continue;
        }
        if (f.type === 'text' || f.type === 'select') {
          vars[f.ref] = el.value;
        } else {
          const n = Number(el.value);
          vars[f.ref] = Number.isFinite(n) ? n : 0;
        }
      }
      // Evaluate outputs in declaration order so later outputs can reference earlier ones.
      for (const out of this.flatOutputs) {
        const ast = this.parsedFormulas[out.ref];
        if (!ast) continue;
        let value;
        try {
          value = evalAst(ast, vars);
        } catch (e) {
          console.warn('lt-calc: error evaluating', out.ref, e);
          value = NaN;
        }
        vars[out.ref] = value;
        const display = this.root.querySelector(`[data-ref="${out.ref}"]`);
        if (display) display.textContent = formatValue(value, out);
      }
      // Gauges run last: they read the outputs the loop above just produced.
      this.updateGauges(vars);
      this.lastVars = vars;
    }
  }

  // Old Calconic embeds double as placeholders, so the native calculator takes over
  // as soon as the theme ships — no body-content edit needed to cut over, and any
  // stray copy of the widget (hidden page, draft article) is covered automatically.
  // Removing the old div afterwards is tidying, not a migration step.
  const LEGACY_CALCONIC = {
    '6801b952c6b845002a324e5b': 'platen-psi' // Platen PSI Calculator
  };

  // Inline embeds: swap each <div class="lt-calc-embed" data-calculator="x">
  // placeholder in rich-text body copy for a clone of the matching <template>
  // emitted by snippets/calculator-embed.liquid. Runs before initAll so the
  // mounted node hydrates on the same pass as any section-based calculator.
  let embedCount = 0;
  function mountEmbeds(scope) {
    const root = scope || document;
    // Match on [data-calculator], NOT .lt-calc-embed[data-calculator]. The Liquid gate
    // in calculator-embed.liquid tests for the data attribute alone, so an editor who
    // pastes the div without the class used to pass the gate (assets downloaded, template
    // emitted) and then mount nothing, silently. Selector and gate now test the same thing.
    // :not(template) — the <template> that HOLDS the calculator also carries
    // data-calculator, so a bare [data-calculator] match treats it as a placeholder,
    // clones it over itself, and mounts a second phantom calculator.
    const placeholders = [
      ...root.querySelectorAll('[data-calculator]:not(template)'),
      ...root.querySelectorAll('[data-calculatorid]')
    ];
    const templates = [...document.querySelectorAll('.lt-calc-template')];

    placeholders.forEach(placeholder => {
      const type = placeholder.dataset.calculator ||
                   LEGACY_CALCONIC[placeholder.dataset.calculatorid];
      if (!type) return; // an unrelated Calconic widget: leave it alone

      // Compare in JS rather than interpolating author-authored body content into a
      // selector string. A crafted data-calculator value (an unbalanced quote) made
      // querySelector throw SyntaxError, and because boot() runs mountEmbeds before
      // initAll, that throw took down EVERY calculator on the page, sections included.
      const tpl = templates.find(t => t.dataset.calculator === type);
      if (!tpl || !tpl.content || !tpl.content.firstElementChild) {
        console.warn('lt-calc: no template for embed', type, placeholder);
        return;
      }
      const node = tpl.content.firstElementChild.cloneNode(true);
      // Unique per instance so field ids stay unique when one page embeds several.
      node.dataset.sectionId = 'lt-calc-' + type + '-' + (++embedCount);
      placeholder.replaceWith(node);
    });
  }

  function initAll(scope) {
    const root = scope || document;
    root.querySelectorAll('.lt-calc').forEach(el => {
      if (el.dataset.ltCalcReady) return;
      el.dataset.ltCalcReady = '1';
      new LtCalculator(el);
    });
  }

  function boot(scope) {
    mountEmbeds(scope);
    initAll(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot());
  } else {
    boot();
  }

  // Re-init on theme-editor section reloads
  document.addEventListener('shopify:section:load', e => boot(e.target));
})();
